import React, { useState, useEffect } from 'react'
import { faArrowRight } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { capitalise } from '../../utilities/constants'
import { selectSkillName, setChallenge, setTargetRole as setTargetRoleAction, selectChallenge, selectTargetRole, updateUserChallenge, updateUserTargetRole, selectIsChallengeSelected, setIsChallengeSelected, setIsTargetRoleSelected, selectIsRoleFlow } from '../../redux/skillSlice'
import ProgressBar from '../../components/ProgressBar'
import { toast } from 'react-toastify'
import OnboardingStep2 from './Step2'
import OnboardingStep3 from './Step3'
import UserDetails from './UserDetails'
import Analysis from './Analysis'
import Pricing from './Pricing'
import { selectInitSessionUserDetails } from '../../redux/onboardingUserDetails'
import { Mixpanel } from '../../config/Mixpanel'
import constants from '../../utilities/constants'
import { selectIsAnalysisPageVisited, selectIsPricingPageVisited } from '../../redux/newAnalysisSlice'
import OnboardingStep4 from './Step4'

const OnboardingV2 = () => {
  const { id } = useParams() // Extract the :id parameter from URL
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()

  // Convert id to step number or use default, or check for special routes
  const step = id ? parseInt(id) || 1 : 1
  const isUserDetailsRoute = location.pathname === '/onboarding/userdetails'
  const isAnalysisRoute = location.pathname === '/onboarding/analysis'
  const isPricingRoute = location.pathname === '/onboarding/pricing'

  // State for managing selected option and role input
  const [selectedOption, setSelectedOption] = useState(null)
  const [showRoleInput, setShowRoleInput] = useState(false)
  const [targetRole, setTargetRole] = useState('')

  // Get skill name from Redux
  const skillName = useSelector(selectSkillName) || 'Data Science'

  // Get current stored values from Redux (for debugging)
  const currentChallenge = useSelector(selectChallenge)
  const currentTargetRole = useSelector(selectTargetRole)
  const userSessionDetails = useSelector(selectInitSessionUserDetails)
  const isChallengeSelected = useSelector(selectIsChallengeSelected)
  const isAnalysisPageVisited = useSelector(selectIsAnalysisPageVisited)
  const isPricingPageVisited = useSelector(selectIsPricingPageVisited)
  const isRoleFlow = useSelector(selectIsRoleFlow)



  useEffect(() => {
    console.log('step' ,step)
  if(location.pathname === '/onboarding/1'){ 
    if(isAnalysisPageVisited){
      console.log('step2')
      navigate('/onboarding/analysis')
      }
  
      else if(isPricingPageVisited){
        console.log('step2')
        navigate('/onboarding/pricing')
      }
    }
    
  }, [dispatch, isAnalysisPageVisited, isPricingPageVisited, location.pathname, navigate, step])

  // Initialize component state from Redux when component mounts
  useEffect(() => {
    // Track Step 1 page load
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP1_PAGE_LOADED, {
      skill: skillName,
      step: step,
      session_id: userSessionDetails?.session_id
    })

    // If we have a challenge stored in Redux, find and set the corresponding option
    if (currentChallenge) {
      const challengeMap = {
        'grow_in_my_career': { id: 1, text: 'Grow In My Career', emoji: '📈'},
        'learn_this_skill': { id: 2, text: 'Learn This Skill', emoji: '🚀' },
        'college_academics': { id: 3, text: 'College / Academics', emoji: '📚' },
        'switch_careers': { id: 4, text: 'Switch Careers', emoji: '♻️' }
      }
      
      const option = challengeMap[currentChallenge]
      console.log('option', option , currentChallenge)
      if (option) {
        setSelectedOption(option)
        
        // Show role input for career-related options
        if (option.id === 1 || option.id === 4) {
          setShowRoleInput(true)
          // If we also have a target role, populate the input
          if (currentTargetRole) {
            setTargetRole(currentTargetRole)
          }
        }
      }
    }
  }, [currentChallenge, currentTargetRole, step, skillName, userSessionDetails?.session_id])

  // Debug: Log current Redux values
  console.log('Current Redux state - Challenge:', currentChallenge, 'Target Role:', currentTargetRole)

  // Define step content dynamically
  const getStepContent = (currentStep) => {

    return {
      title: `What's your main goal with ${capitalise(skillName)}?`,
      subtitle: "",
      options: [
        { id: 1, text: 'Grow In My Career', emoji: '📈' , value : 'grow_in_my_career' },
        { id: 2, text: 'Learn This Skill', emoji: '🚀' , value : 'learn_this_skill'},
        { id: 3, text: 'College / Academics', emoji: '📚' , value : 'college_academics' },
        { id: 4, text: 'Switch Careers', emoji: '♻️' , value : 'switch_careers' }
      ]
    }

  }

  const currentContent = getStepContent(step)

  const handleOptionClick = async (option) => {
    const sessionId = userSessionDetails?.session_id

    if (!sessionId) {
      toast.error('Your session is still being created. Please try again.')
      return
    }

    console.log(`Step ${step} - Selected Goal:`, option.text)

    // Track goal selection in Step 1
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP1_GOAL_SELECTED, {
      goal: option.text,
      skill: skillName,
      session_id: userSessionDetails?.session_id
    })

    try {
      await dispatch(updateUserChallenge({ session_id: sessionId, challenge: option.value })).unwrap()
    } catch (error) {
      toast.error('Failed to save your goal. Please try again.')
      return
    }

    setSelectedOption(option)
    dispatch(setChallenge(option.value))
    
    if (option.id === 1 || option.id === 4) {
      if(isRoleFlow){
        dispatch(setIsTargetRoleSelected(false))
        navigate('/onboarding/2')
        return;
      }
      setShowRoleInput(true)
      dispatch(setIsTargetRoleSelected(true))
      dispatch(setIsChallengeSelected(true))
    } else {
      dispatch(setIsTargetRoleSelected(false))
      setTimeout(() => {
        dispatch(setIsTargetRoleSelected(false))
        dispatch(setIsChallengeSelected(false))
        navigate('/onboarding/2')
      }, 500)
    }
  }

  const handleRoleInputChange = (e) => {
    setTargetRole(e.target.value)
  }

  const handleRoleSubmit = async () => {
    if (!targetRole.trim()) {
      toast.error('Please enter the role you are aiming for')
      return
    }

    if (!userSessionDetails?.session_id) {
      toast.error('Session not found. Please refresh and try again.')
      return
    }

    console.log('Saving to Redux - Goal:', selectedOption.text, 'Role:', targetRole.trim())

    // Track role submission in Step 1
    Mixpanel.track(constants.Mixpanel.ONBOARDING_V2_STEP1_ROLE_SUBMITTED, {
      goal: selectedOption.text,
      role: targetRole.trim(),
      skill: skillName,
      session_id: userSessionDetails?.session_id
    })

    // Store the role in Redux and navigate to step 2
    dispatch(setTargetRoleAction(targetRole.trim()))

    try {
      await dispatch(updateUserTargetRole({ session_id: userSessionDetails?.session_id, role: targetRole.trim() })).unwrap()
    } catch (e) {
      toast.error('Failed to save role. Please try again.')
      return
    }

    navigate('/onboarding/2')
  }

  // Conditional rendering based on step or special routes
  if (isUserDetailsRoute) {
    return <UserDetails />
  }

  if (isAnalysisRoute) {
    return <Analysis />
  }

  if (isPricingRoute) {
    return <Pricing />
  }

  if (step === 2) {
    return <OnboardingStep2 />
  }

  if (step === 3) {
    return <OnboardingStep3 />
  }

  if (step === 4) {
    // Redirect to MCQ with question 1
    return <OnboardingStep4 />
  }

  return (
    <div className='mt-8 w-full md:max-w-[45rem] flex flex-col justify-between items-center flex-1 mx-auto px-4 pb-4 lg:pb-6 lg:px-6 text-white relative gap-6'>
      {/* Progress bar and step indicator */}
      <div className='flex flex-col justify-start items-center w-full'>
        <ProgressBar
          currentStep={isRoleFlow ? 3 : 1}
          totalSteps={isRoleFlow ? 8 : 6}
          stepText={
            `Step ${isRoleFlow ? 3 : 1}/${isRoleFlow ? 8 : 6}: mastering ${capitalise(skillName)}`
          }
        />

        <div className='w-full mt-4 flex flex-col justify-start items-center p-[2px] rounded-[1.5rem]' style={{
          backgroundImage: `linear-gradient(
            to right,
            rgba(64, 106, 255, 0.3) 0%,
            rgba(64, 106, 255, 1) 56%,
            rgba(64, 106, 255, 0.3) 100%
          )`
        }}>
          {/* Main content card */}
          <div className='flex flex-col p-6 rounded-[1.5rem]  shadow-[0px_0px_10px_0px_rgba(0,0,0,0.5)] w-full relative' style={{ backgroundImage: `radial-gradient(circle, #0B305F 0%, #0A1E3F 100%)` }}>
            <div className="absolute inset-0 opacity-40 rounded-3xl bg-repeat bg-[length:180px_180px] pointer-events-none"
              style={{ backgroundImage: `url('https://assets.learntube.ai/files/personalise/web.webp')` }} />

            {/* Background overlay */}
            <div className='absolute inset-0 pointer-events-none rounded-[1.5rem]'>
              <div className='absolute bg-[#406AFF]/15 inset-0 rounded-[1.5rem]'></div>
            </div>

            {/* Content */}
            <div className='w-full flex flex-col relative z-10'>

              {/* Show Role Input Screen for Career Options */}
              {showRoleInput && isChallengeSelected ? (
                <div className='flex flex-col gap-6'>
                  {/* Goal display */}
                  <div className='flex items-center gap-1 text-[14px]'>
                    <span className='text-white/50 font-medium'>Goal:</span>
                    <span className='text-white capitalize'>{selectedOption.text}</span>
                  </div>

                  {/* Role input section */}
                  <div className='flex flex-col gap-4'>
                    <div className='flex flex-col'>
                      <h1 className='bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[1.2]'>
                        Which role are you aiming for?
                      </h1>
                    </div>

                    {/* Role input field */}
                    <div className='bg-[#406AFF]/10 border border-[#7FC241] flex flex-col items-start overflow-hidden h-[49px] rounded-lg w-full'>
                      <input
                        type='text'
                        value={targetRole}
                        autoFocus={true}
                        onChange={handleRoleInputChange}
                        placeholder='Enter your target role...'
                        className='bg-transparent h-full bg-[#406AFF]/10 text-white text-[14px] w-full outline-none border-none placeholder-white/50'
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleRoleSubmit()
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Submit button */}
                  <button
                    onClick={handleRoleSubmit}
                    disabled={!targetRole.trim()}
                    className={`${!targetRole.trim()
                        ? 'bg-[#7FC241]/50 cursor-not-allowed'
                        : 'bg-[#7FC241] border-b-2 border-l-2 border-r-2 border-[#8bd845] hover:bg-[#8bd845]'
                      }  h-12 rounded-lg font-semibold text-[#121212] text-lg transition-colors flex items-center justify-center gap-2`}
                  >
                    <span>Start Personalising Now</span>
                    <FontAwesomeIcon icon={faArrowRight} className='text-sm' />
                  </button>
                </div>
              ) : (
                <>
                  {/* Header */}
                  <div className='w-full flex flex-col gap-6'>
                    <div className='flex flex-col'>
                      <h1 className='bg-gradient-to-r from-white to-[#FFE994] bg-clip-text text-transparent font-bold text-[1.75rem] leading-[1.2] mb-0'>
                        {currentContent.title}
                      </h1>
                    </div>
                    <p className='text-[0.875rem] text-white/50 leading-[1.4]'>
                      {currentContent.subtitle}
                    </p>
                  </div>

                  {/* Options */}
                  {currentContent.options.length > 0 && (
                    <div className='w-full flex flex-col gap-3'>
                      {currentContent.options.map((option, index) => {
                        const isSelected = selectedOption?.id === option.id
                        return (
                          <div
                            key={index}
                            className={`w-full flex gap-2 items-center p-4 rounded-lg w-full cursor-pointer transition-colors group ${
                              isSelected 
                                ? 'bg-[#7FC241]/20 border border-[#7FC241] shadow-[0px_0px_2px_0px_#7fc241]' 
                                : 'bg-[#406AFF]/15 border border-[#406AFF]/70 hover:bg-[#406AFF]/25'
                            }`}
                            onClick={() => handleOptionClick(option)}
                          >
                            <div className={`w-[14px] h-[14px] border-2 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                              isSelected 
                                ? 'border-[#7FC241]' 
                                : 'border-[#70AEFF]'
                            }`}>
                              <div className={`w-1.5 h-1.5 rounded-full transition-all ${
                                isSelected 
                                  ? 'bg-[#7FC241] opacity-100' 
                                  : 'bg-[#70AEFF] opacity-0'
                              }`}></div>
                            </div>
                            <div className='flex items-center justify-between flex-1 text-[0.875rem] text-white'>
                              <span>{option.text}</span>
                              <span>{option.emoji}</span>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Bottom certification info */}
      <div className='flex flex-col gap-3 items-center justify-center w-full mt-6'>
        {/* Company logos */}
        <div className='flex items-center pr-2'>
          <div className='w-[66px] h-[28px]'>
            <img alt="Company logo" className='w-full h-full object-cover' src="https://assets.learntube.ai/files/personalise/Frame%20427321976%20(2).webp" />
          </div>
        </div>

        {/* Certification text */}
        <div className='text-center'>
          <p className='font-medium italic text-[0.75rem] text-[#83BEFF] leading-[1.4]'>
            +1,100 companies recognise this certificate for roles in {capitalise(skillName)}
          </p>
        </div>
      </div>
    </div>
  )
}

export default OnboardingV2
